import React, { useCallback } from "react";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEye } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Checkbox as CheckboxUI } from "semantic-ui-react";

import { BillingIdentity } from "~/billing";
import MeetingFeedbackModal from "~/feedback/MeetingFeedbackModal";
import { useID } from "~/lib/hooks";
import type { StreamSettingsResponseStream } from "~/stream/StreamSettings/index";
import { colors, useModals } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import Popup from "~/ui/legacy/Popup";
import { ButtonLink } from "~/ui/legacyFellowIndex";

import type { StreamSettingsModalQuery } from "~/__generated__/StreamSettingsModalQuery.graphql";

import stylessettings from "~/settings/css/settings.module.css";
import styles from "~/stream/StreamSettings/StreamSettings.module.css";

interface MeetingFeedbackProps {
	stream: StreamSettingsResponseStream;
	viewer: StreamSettingsModalQuery["response"]["viewer"];
	onChange: (e: React.FormEvent<HTMLInputElement>, { checked }: { checked?: boolean }) => void;
	withBorder?: boolean;
}

export function MeetingFeedbackPrompt({
	streamId,
	onChange,
	onAnonymousChange,
	withBorder,
	stream,
	viewer,
}: MeetingFeedbackProps & {
	streamId: string;
	onAnonymousChange: (e: React.FormEvent<HTMLInputElement>, { checked }: { checked?: boolean }) => void;
}) {
	const toggleID = useID();
	const anonymousToggleId = useID();
	const modals = useModals();

	const onPreview = useCallback(() => {
		modals.open(({ close }) => <MeetingFeedbackModal streamId={streamId} close={close} isPreview />);
	}, [modals, streamId]);
	const onAnonymousChangeReact = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const checked = e.target.checked;
			return onAnonymousChange(e, { checked });
		},
		[onAnonymousChange],
	);

	return (
		<section className={cx(styles.feedbackBlock, { [styles.withBorder]: withBorder })}>
			<div className={styles.row}>
				<h1 className={styles.title}>
					Request Meeting Feedback
					<Popup
						inverted
						size="tiny"
						content="Feedback responses will be shared with all participants except guests"
						position="top center"
						trigger={<FontAwesomeIcon icon={faInfoCircle} />}
						className={styles.popup}
					/>
				</h1>
				<ButtonLink onClick={onPreview} color={colors.royal[600]} className={styles.previewLink}>
					<FontAwesomeIcon icon={faEye} /> Preview
				</ButtonLink>
			</div>
			<BillingIdentity feature="meeting_feedback" viewer={viewer}>
				{(extraProps, icon, paywalled) => (
					<div className={styles.row} {...extraProps}>
						<label htmlFor={toggleID} className={styles.label}>
							Enable prompts for participants to give feedback on the meetings in this Stream…
						</label>
						<CheckboxUI
							toggle
							checked={stream?.isMeetingFeedbackPromptEnabled ?? true}
							onChange={paywalled ? undefined : onChange}
							id={toggleID}
							className={cx(styles.toggle, stylessettings.settingsPageToggle)}
							disabled={paywalled}
						/>
						{icon}
					</div>
				)}
			</BillingIdentity>
			<BillingIdentity feature="meeting_feedback" viewer={viewer}>
				{(extraProps, icon, paywalled) => (
					<div className={cx(styles.row, styles.rowAnonymous)} {...extraProps}>
						<Checkbox
							green
							id={anonymousToggleId}
							checked={stream?.isMeetingFeedbackAnonymous}
							onChange={paywalled ? undefined : onAnonymousChangeReact}
						></Checkbox>
						<label htmlFor={anonymousToggleId} className={styles.label}>
							Anonymous responses{" "}
							<Popup
								inverted
								size="tiny"
								content="This will apply to all meetings that don’t already have feedback."
								position="top center"
								trigger={<FontAwesomeIcon icon={faInfoCircle} />}
								className={styles.popup}
							/>
						</label>
					</div>
				)}
			</BillingIdentity>
		</section>
	);
}

export function MeetingFeedbackTab({ stream, onChange, withBorder, viewer }: MeetingFeedbackProps) {
	const toggleID = useID();

	return (
		<section className={cx(styles.feedbackBlock, { [styles.withBorder]: withBorder })}>
			<h1 className={styles.title}>
				Feedback tab
				<Popup
					inverted
					size="tiny"
					content="This tab will be visible to all members in this stream"
					position="top center"
					trigger={<FontAwesomeIcon icon={faInfoCircle} />}
					className={styles.popup}
				/>
			</h1>
			<BillingIdentity feature="meeting_feedback" viewer={viewer}>
				{(extraProps, icon, paywalled) => (
					<div className={styles.row} {...extraProps}>
						<label htmlFor={toggleID} className={styles.label}>
							Show a Feedback tab in this Stream…
						</label>
						<CheckboxUI
							toggle
							checked={stream?.isMeetingFeedbackTabEnabled}
							onChange={paywalled ? undefined : onChange}
							id={toggleID}
							className={cx(styles.toggle, stylessettings.settingsPageToggle)}
							disabled={paywalled}
						/>
						{icon}
					</div>
				)}
			</BillingIdentity>
			<p className={styles.note}>
				<b>Note:</b> Feedback will be filtered to only show Feedback for this meeting.
			</p>
		</section>
	);
}
